import React, { useImperativeHandle, useState, useEffect } from 'react';
import { useReactive } from 'ahooks';
import { Input, Button, message, MessageBox } from 'kenshin';
import Dropdown from '@/components/Dropdown';
import { PlusOutlined, SearchOutlined, MoreOutlined } from '@ant-design/icons';
import {
  getAiSessionPage,
  deleteAiSession,
  updateAiSession,
} from '@/_serveice/aiCreation';
import './index.less';

// 获取当前员工ID
function getEmpId() {
  try {
    const emp = JSON.parse(localStorage.getItem('employeeDTO'));
    return emp?.empId;
  } catch {
    return null;
  }
}

const ConversationList = React.forwardRef((props, ref) => {
  const { currentId, onSelect, onNew } = props;
  const [searchVisible, setSearchVisible] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [menuVisible, setMenuVisible] = useState(null);
  const [editingId, setEditingId] = useState(null); // 正在编辑的会话ID
  const [editingValue, setEditingValue] = useState(''); // 编辑中的文字
  const [savingRename, setSavingRename] = useState(false); // 重命名保存中

  const state = useReactive({
    conversations: [],
    loading: false,
  });

  // 加载会话列表
  const loadConversations = async () => {
    const empId = getEmpId();
    if (!empId) return;

    state.loading = true;
    try {
      const res = await getAiSessionPage({
        empId,
        page: {
          pageNo: 1,
          pageSize: 50,
          sortFields: [{ name: 'lastChatTime', value: 1 }], // 1-倒序
        },
        sessionName: searchValue || undefined,
      });

      if (res?.success && res?.data?.datas) {
        state.conversations = res.data.datas;
      } else {
        state.conversations = [];
      }
    } catch (err) {
      console.error('加载会话列表失败:', err);
      message.error('加载会话列表失败');
    } finally {
      state.loading = false;
    }
  };

  // 首次加载
  useEffect(() => {
    loadConversations();
  }, []);

  // 搜索时重新加载
  useEffect(() => {
    loadConversations();
  }, [searchValue]);

  useImperativeHandle(ref, () => ({
    refresh: loadConversations,
  }));

  // 右键菜单操作
  const handleMenuClick = async ({ key }, conv) => {
    setMenuVisible(null);

    switch (key) {
      case 'rename': {
        // 进入编辑模式
        setEditingId(conv.sessionId);
        setEditingValue(conv.sessionName || '新对话');
        break;
      }
      case 'pin': {
        const res = await updateAiSession({
          sessionId: conv.sessionId,
          sessionName: conv.sessionName || '新对话', // 保留原名称
          isTop: conv.isTop === 1 ? 0 : 1,
        });
        if (res?.success) {
          message.success(conv.isTop === 1 ? '已取消置顶' : '已置顶');
          loadConversations();
        } else {
          message.error(res?.errorMessage || '操作失败');
        }
        break;
      }
      case 'delete': {
        MessageBox.confirm({
          title: '确认删除',
          content: '删除后无法恢复，确定要删除该对话吗？',
          onOk: async () => {
            const res = await deleteAiSession({ sessionId: conv.sessionId });
            if (res?.success) {
              message.success('已删除');
              if (currentId === conv.sessionId) {
                onNew();
              }
              loadConversations();
            } else {
              message.error(res?.errorMessage || '删除失败');
            }
          },
        });
        break;
      }
      default:
        break;
    }
  };

  // 保存重命名
  const saveRename = async (conv) => {
    if (!editingValue.trim()) {
      setEditingId(null);
      return;
    }

    if (editingValue.trim() === (conv.sessionName || '新对话')) {
      setEditingId(null);
      return;
    }

    setSavingRename(true);
    const empId = getEmpId();
    const res = await updateAiSession({
      sessionId: conv.sessionId,
      sessionName: editingValue.trim(),
      isTop: conv.isTop || 0, // 保留原置顶状态
    });
    setSavingRename(false);

    if (res?.success) {
      message.success('重命名成功');
      loadConversations();
    } else {
      message.error(res?.errorMessage || '重命名失败');
    }
    setEditingId(null);
  };

  const menuItems = (conv) => [
    {
      key: 'rename',
      label: '重命名',
      onClick: () => handleMenuClick({ key: 'rename' }, conv),
    },
    {
      key: 'pin',
      label: conv.isTop === 1 ? '取消置顶' : '置顶',
      onClick: () => handleMenuClick({ key: 'pin' }, conv),
    },
    {
      key: 'delete',
      label: '删除',
      danger: true,
      onClick: () => handleMenuClick({ key: 'delete' }, conv),
    },
  ];

  const filteredConversations = state.conversations.filter((c) =>
    (c.sessionName || '新对话')
      .toLowerCase()
      .includes(searchValue.toLowerCase()),
  );

  return (
    <div className="conversationList">
      {/* Loading 遮罩 */}
      {savingRename && (
        <div className="savingOverlay">
          <div className="savingContent">
            <div className="savingSpinner" />
            <div className="savingText">保存中...</div>
          </div>
        </div>
      )}

      {/* 顶部操作栏 */}
      <div className="listHeader">
        <Button
          onClick={() => {
            onNew();
          }}
          className="newBtn"
        >
          新建对话
        </Button>
        {/* <div className="headerActions">
          {searchVisible ? (
            <Input
              size="small"
              placeholder="搜索对话..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              autoFocus
              onBlur={() => {
                if (!searchValue) setSearchVisible(false);
              }}
              className="searchInput"
              prefix={<SearchOutlined />}
            />
          ) : (
            <SearchOutlined
              className="searchIcon"
              onClick={() => setSearchVisible(true)}
            />
          )}
        </div> */}
      </div>

      {/* 对话列表 */}
      <div className="listBody">
        {filteredConversations.map((conv) => (
          <div
            key={conv.sessionId}
            className={`convItem ${
              currentId === conv.sessionId ? 'active' : ''
            }`}
            onClick={() => onSelect(conv.sessionId)}
          >
            <div className="convThumb">
              {getFirstThumbUrl(conv.itemList) ? (
                <img src={getFirstThumbUrl(conv.itemList)} alt="" />
              ) : (
                <div className="convThumbPlaceholder">AI</div>
              )}
            </div>
            <div className="convInfo">
              {editingId === conv.sessionId ? (
                <Input
                  size="small"
                  value={editingValue}
                  onChange={(e) => setEditingValue(e.target.value)}
                  onBlur={() => saveRename(conv)}
                  onPressEnter={() => saveRename(conv)}
                  autoFocus
                  className="editInput"
                  onClick={(e) => e.stopPropagation()}
                />
              ) : (
                <div className="convTitle">{conv.sessionName || '新对话'}</div>
              )}
              <div className="convTime">{formatTime(conv.lastChatTime)}</div>
            </div>
            <Dropdown
              items={menuItems(conv)}
              trigger="click"
              visible={menuVisible === conv.sessionId}
              onVisibleChange={(v) => setMenuVisible(v ? conv.sessionId : null)}
            >
              <div className="moreBtn">
                <MoreOutlined />
              </div>
            </Dropdown>
          </div>
        ))}
        {filteredConversations.length === 0 && !state.loading && (
          <div className="emptyTip">暂无对话</div>
        )}
        {state.loading && <div className="emptyTip">加载中...</div>}
      </div>
    </div>
  );
});

// 工具函数：格式化时间
function formatTime(timeStr) {
  if (!timeStr) return '';
  const date = new Date(timeStr);
  const now = new Date();
  const diff = now - date;

  if (diff < 60000) return '刚刚';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}分钟前`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}小时前`;
  if (diff < 604800000) return `${Math.floor(diff / 86400000)}天前`;

  return `${date.getMonth() + 1}/${date.getDate()}`;
}

// 工具函数：获取第一张缩略图
function getFirstThumbUrl(itemList) {
  if (!itemList || itemList.length === 0) return null;
  const firstItem = itemList[0];
  if (firstItem.outputList && firstItem.outputList.length > 0) {
    return firstItem.outputList[0].resourceUrl;
  }
  return null;
}

export default ConversationList;
